import throttle from 'lodash-es/throttle';
import {
  isEnoughForTargetAppVersion,
  platform,
  web2app,
} from '@kcd/app-interface';
import { trackEvent } from '@kcd/event-tracker';

import { ENV } from '~/utils/env';

/**
 * 앱 웹뷰에서 새로운 url을 모달(웹뷰 스택)로 열 때 사용하는 래퍼.
 *
 * `web2app.openModal`을 직접 호출하지 말고 항상 이 함수를 사용한다.
 *
 * 동작:
 * - **절대 URL(`http`/`https`로 시작)**: url을 그대로 모달로 연다.
 * - **상대경로(`/...`)**: 현재 도메인(`window.location.origin`)을 붙여
 *   절대경로로 변환한 뒤 모달로 연다. → "현재 도메인의 페이지 열기"에 사용.
 * - `fallback`: 앱 웹뷰가 아니거나 모달을 열 수 없을 때 실행되는 대체 동작.
 * - 연속 호출로 모달이 중복으로 열리는 것을 막기 위해 300ms `throttle` 적용.
 *
 * @param url 이동할 경로. 절대 URL(`http`/`https`) 또는 현재 도메인 기준 상대경로 모두 허용
 * @param fallback 앱 모달을 열 수 없을 때 실행할 대체 동작
 *
 * @example
 * // 현재 도메인의 페이지
 * openPageModal('/event/recommend/pick/123');
 * // 외부/절대 URL
 * openPageModal('https://www.ftc.go.kr/...');
 */
export const openPageModal: (url: string, fallback?: () => void) => void =
  throttle((url: string, fallback?: () => void) => {
    // 절대 URL(http/https)이면 그대로 열어준다.
    if (url.startsWith('http')) {
      web2app.openModal({
        url,
        fallback,
      });
      return;
    }

    // 상대경로면 현재 origin을 붙여 절대경로로 만들어 열어준다.
    const path = decodeURIComponent(
      new URL(`${window.location.origin}${url}`).toString(),
    );

    web2app.openModal({
      url: path,
      fallback,
    });
  }, 300);

/**
 * url을 앱 내부 모달이 아닌 **외부 브라우저**로 여는 래퍼.
 *
 * `web2app.openExternalBrowser`를 직접 호출하지 말고 항상 이 함수를 사용한다.
 * (외부 결제/약관/제휴 페이지 등 인앱 웹뷰가 아닌 시스템 브라우저로 열어야 할 때)
 *
 * @param url 외부 브라우저로 열 절대 URL
 */
export function openExternalBrowser(url: string) {
  web2app.openExternalBrowser({ url });
}

/**
 * 현재 열려 있는 앱 모달(웹뷰)을 닫는 래퍼.
 *
 * `web2app.closeCurrentModal`을 직접 호출하지 말고 항상 이 함수를 사용한다.
 *
 * @param fallback 모달을 닫을 수 없을 때 실행할 대체 동작. 미지정 시 `window.close()`
 */
export function closeCurrentModal(fallback?: () => void) {
  web2app.closeCurrentModal({ fallback: fallback ?? (() => window.close()) });
}

export function appAlert(options?: { title?: string; description?: string }) {
  const description =
    options?.description ||
    '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';

  web2app.openDialog({
    type: 'alert',
    title: options?.title,
    description,
    // eslint-disable-next-line no-alert
    fallback: () => window.alert(description),
  });
}
export const MARKET_TAB_APP_SUPPORT_VERSION = {
  android: '2.2221.109',
  ios: '2.2222.211',
};

export function isLoadedInAppHavingMarketTab() {
  const currentPlatform = platform();
  if (!(currentPlatform === 'ios' || currentPlatform === 'android')) {
    return false;
  }

  return isEnoughForTargetAppVersion(
    MARKET_TAB_APP_SUPPORT_VERSION[currentPlatform],
  );
}

export function isVisibleMoreMenu() {
  const currentPlatform = platform();
  // 웹뷰에서만 노출
  if (!(currentPlatform === 'ios' || currentPlatform === 'android')) {
    return false;
  }

  // iOS 2.2450.851 버전부터 더보기 메뉴 앱 인터페이스 추가됌
  if (currentPlatform === 'ios' && !isEnoughForTargetAppVersion('2.2449.851')) {
    return false;
  }

  return true;
}

export const APP_NAME = {
  ios: 'CashNote Ifrit',
  android: 'CashNote Albatross',
} as const;

export function getCashnoteAppVersion(
  userAgent: string,
  appPlatform: keyof typeof APP_NAME,
) {
  const result = new RegExp(`${APP_NAME[appPlatform]}/(\\d+.\\d+.\\d+)`).exec(
    userAgent,
  );

  if (!result) {
    return null;
  }

  return result[1];
}

const DEV_CASHNOTE_AUTH_URL = 'https://dev-app.cashnote.kr/auth';
const CASHNOTE_AUTH_URL = 'https://app.cashnote.kr/auth';

export const getAuthUrl = (redirectUrl?: string) => {
  const url = ENV === 'dev' ? DEV_CASHNOTE_AUTH_URL : CASHNOTE_AUTH_URL;

  const currentHref = redirectUrl || window.location.href;

  return `${url}?returnUrl=${currentHref}&utm_source=system&utm_medium=login&utm_campaign=market_241118_login`;
};

export function openAllMenu() {
  trackEvent('clicked header menu', {
    name: '전체메뉴',
    page: '마켓',
  });

  web2app.openAllMenu();
}
